import type { Agreement, AgreementType } from "types/index";
import Grid from "@mui/material/Grid2";
import { Button, FormGroup } from "@cofano/cck";
import { intl } from "LocaleWrapper";
import { Box, Skeleton } from "@mui/material";
import AgreementCreateTemplateDialog from "view/contractsNew/components/dialogs/AgreementCreateTemplateDialog";
import { Fragment, ReactNode, useState } from "react";
import { FormArrayContainer, FormArrayRow } from "components/cck/FormArray";
import { useStoreState, Session } from "ezStore";
import { DataFormat } from "utils";
import type { GetAgreementsQuery } from "__generated__/graphql";
import CalculationMethodDetails from "view/calculationmethods/CalculationMethodDetails";
import AgreementConditionDetails from "view/agreementconditions/AgreementConditionDetails";
import { AgreementSchema } from "view/agreements/schema";
import { useQuery } from "@apollo/client";
import { getAgreementListMasterData_query, getAgreements_query } from "view/agreements/utils/AgreementQueries";
import { ChildProps } from "types/commons";
import {
	getAgreementCardActions,
	getAgreementCardColor,
	getAgreementCardSubtitle,
	getAgreementCardTitle,
	sortAgreements,
} from "view/agreements/utils/AgreementUtils";
import { CharterSubtype, CharterType } from "__generated__/enums";
import { CurrencyFragment } from "__generated__/graphql/fragments.generated";

export type AgreementsDetailsProps = {
	charterType: CharterType;
	charterSubtype: CharterSubtype;
	contractId?: number;
	currency: CurrencyFragment;
	preobtainedAgreements?: {
		queryData: ChildProps<GetAgreementsQuery, "agreements">;
		loading: boolean;
	};
	otherContractId?: number;
	showCreateTemplateButton?: boolean;
	voyageId?: number;
}

type DetailsProps = {
	agreement: Agreement;
	session: Session;
	currency: CurrencyFragment;
}

type DetailsField = {
	id: string;
	render: () => ReactNode;
}

const AgreementDetails = ({ charterType, charterSubtype, contractId, currency, preobtainedAgreements, showCreateTemplateButton = true, voyageId }: AgreementsDetailsProps) => {
	const session = useStoreState(store => store.session);
	const favorites = useStoreState(store => store.session.user?.properties.favorites);

	const [ openDialog, setOpenDialog ] = useState(false);

	const { data: masterData, loading: masterDataLoading } = useQuery(getAgreementListMasterData_query);
	const agreementTypes = masterData?.agreementTypes as AgreementType[] ?? [];

	const { data, loading: agreementsLoading } = useQuery(getAgreements_query, {
		variables: {
			search: {
				contractId: contractId,
			},
		},
		skip: !contractId || !!preobtainedAgreements,
	});

	const loading = agreementsLoading || masterDataLoading || preobtainedAgreements?.loading;
	const agreements = preobtainedAgreements?.queryData ?? data?.agreements ?? [];
	const sortedAgreements = sortAgreements(favorites ?? [], agreementTypes, agreements);

	return (
		<>
			<Grid container spacing={2}>
				<Grid size={12}>
					{loading && (
						<div style={{ padding: 16 }}>
							{Array.from(Array(5)).map((index) => (
								<Skeleton
									variant="text"
									width="100%"
									height={50}
									style={{ margin: "0, 10" }}
									key={index} />
							))}
						</div>
					)}

					{showCreateTemplateButton && sortedAgreements.length > 0 && (
						<Grid size={12} sx={{ display: "flex", justifyContent: "end" }}>
							<Box sx={{ padding: "16px" }}>
								<Button onClick={() => setOpenDialog(true)} size="small" variant={"text"}>
									{intl.formatMessage({ id: "CREATE_TEMPLATE" })}
								</Button>
							</Box>
						</Grid>
					)}

					<FormArrayContainer>
						{sortedAgreements.map((a) => {
							return (
								<FormArrayRow
									color={getAgreementCardColor(a)}
									collapsed
									title={getAgreementCardTitle(a)}
									subtitle={getAgreementCardSubtitle(a)}
									onCustomActions={getAgreementCardActions(a, voyageId)}
									key={a.id}>
									<Grid container spacing={2}>
										<Details agreement={a} session={session} currency={currency} />
									</Grid>
								</FormArrayRow>
							);
						})}
					</FormArrayContainer>
				</Grid>
			</Grid>

			<AgreementCreateTemplateDialog
				open={openDialog}
				onClose={() => setOpenDialog(false)}
				agreements={agreements}
				compatibleCharterType={charterType}
				compatibleCharterSubtype={charterSubtype}
			/>
		</>
	);
};

const Details = ({ agreement, session, currency }: DetailsProps) => {
	const schema = AgreementSchema[agreement.type];

	const hiddenFields = Object.entries(schema?.agreementFields ?? {}).filter(([ , x ]) => {
		const rules = x.rules;
		return rules?.hidden || rules?.hiddenIf?.({ calculationMethodType: agreement.calculationMethod?.type ?? null });
	}).map(([ id ]) => id);

	const AgreementDetailsFields: DetailsField[] = [
		{
			id: "agreement.costType",
			render: () => (
				<Grid size={4}>
					<FormGroup label={intl.formatMessage({ id: "COST_TYPE" })} overflow="hidden">
						{DataFormat.costType(agreement.costType, session.locale)}
					</FormGroup>
				</Grid>
			),
		},
		{
			id: "agreement.description",
			render: () => (
				<Grid size={4}>
					<FormGroup label={intl.formatMessage({ id: "DESCRIPTION" })} overflow="hidden">
						{agreement.description || "-"}
					</FormGroup>
				</Grid>
			),
		},
		{
			id: "agreement",
			render: () => schema?.Details && (
				<schema.Details agreement={agreement} hiddenFields={hiddenFields} />
			),
		},
		{
			id: "agreement.calculationMethod",
			render: () => agreement.calculationMethod && (
				<CalculationMethodDetails
					agreementType={agreement.type}
					calculationMethod={agreement.calculationMethod}
					currency={currency}
					session={session} />
			),
		},
		{
			id: "agreement.invoiceTo",
			render: () => (
				<Grid size={4}>
					<FormGroup label={intl.formatMessage({ id: "INVOICE_TO" })} overflow="hidden">
						{agreement.invoiceTo?.name || "-"}
					</FormGroup>
				</Grid>
			),
		},
		{
			id: "agreement.conditions",
			render: () => agreement.conditions && agreement.conditions.length > 0 && (
				<AgreementConditionDetails conditions={agreement.conditions} />
			),
		},
	];

	return <>
		{AgreementDetailsFields
			.filter(x => {
				const rules = schema?.agreementFields?.[x.id]?.rules;
				return !rules?.hidden && !rules?.hiddenIf?.({ calculationMethodType: agreement.calculationMethod?.type ?? null });
			})
			.map(x => (
				<Fragment key={x.id}>
					{x.render()}
				</Fragment>
			))
		}
	</>;
};

export default AgreementDetails;
